import { api } from '@/lib/api'
import {
  SCRIBE_MAX_AUDIO_BASE64_CHARS,
  type ScribeCaptureResult,
  type ScribeGuidedSessionRequest,
  type ScribeGuidedSessionResponse,
  type ScribeTargetField,
} from '@tenfour/contracts'

// Scribe capture client (plans/scribe.md). Two routes on purpose: the authed
// surfaces stamp the account onto metrics; the signup variant is public and
// rides the outreach throttle/cap posture. Both accept audio OR text.

export type ScribeCapturePayload = {
  audio?: string
  mimeType?: string
  text?: string
  durationMs?: number
  fields: ScribeTargetField[]
  context?: string
}

export type { ScribeCaptureResult, ScribeTargetField }

export function captureScribe(payload: ScribeCapturePayload): Promise<ScribeCaptureResult> {
  return api('/scribe/capture', { method: 'POST', body: JSON.stringify(payload) })
}

export function captureSignupScribe(payload: ScribeCapturePayload): Promise<ScribeCaptureResult> {
  return api('/scribe/signup-capture', { method: 'POST', body: JSON.stringify(payload) })
}

export function createScribeGuidedSession(
  payload: ScribeGuidedSessionRequest,
): Promise<ScribeGuidedSessionResponse> {
  return api('/scribe/session', { method: 'POST', body: JSON.stringify(payload) })
}

export function createPublicScribeGuidedSession(
  payload: ScribeGuidedSessionRequest,
): Promise<ScribeGuidedSessionResponse> {
  return api('/scribe/public-session', { method: 'POST', body: JSON.stringify(payload) })
}

/**
 * Blob → bare base64 (no `data:` prefix), with the client-side pre-flight
 * against the shared cap so an over-long recording fails with a friendly
 * message instead of a 400.
 */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read the recording.'))
    reader.onload = () => {
      const url = String(reader.result ?? '')
      const comma = url.indexOf(',')
      const base64 = comma >= 0 ? url.slice(comma + 1) : url
      if (base64.length > SCRIBE_MAX_AUDIO_BASE64_CHARS) {
        reject(new Error('That recording is too long — try a shorter take or type instead.'))
        return
      }
      resolve(base64)
    }
    reader.readAsDataURL(blob)
  })
}
